import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import * as Colors from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {JSONObject, JSONValue} from 'util/Types';

import {
  getConfigShapeAtConfigShapePath,
  maybeGetValueFromConfig,
} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  ArrayExpandableItemControls,
  EditorExpandableDetailsConfig,
  isFieldUISchemaWithStandardComponent,
  ShapeUISchema,
  SummaryExpandableDetailsConfig,
} from '../StepConfigEditorTypes';
import {StepConfigField, VariableHeaderOrNull} from './StepConfigFieldsTemplate';

/** A single resolved summary header entry: the config value plus the config-shape kind that drives rendering. */
export interface SummaryField {
  kind: PipelineConfigJsonSpec['kind'];
  value: JSONValue | null;
}

/**
 * Resolves each `summary`-mode header field to its value and config-shape kind
 */
export function resolveSummaryFields(
  headerFields: Array<string>,
  uiSchema: ShapeUISchema,
  configShape: PipelineConfigJsonSpec,
  config: JSONObject
): Array<SummaryField> {
  return headerFields.map((fieldName) => {
    const field = uiSchema[fieldName];
    assert(
      isFieldUISchemaWithStandardComponent(field),
      `Summary header field "${fieldName}" must be a direct field of the section with a configShapePath`
    );
    return {
      value: maybeGetValueFromConfig(config, field.configShapePath),
      kind: getConfigShapeAtConfigShapePath(field.configShapePath, configShape, config).kind,
    };
  });
}

export const EXPAND_TOGGLE_VALUE = '__EXPAND_TOGGLE_VALUE__';
const isExpandedGlobalState = makeUseGlobalClientState<boolean | null>(null);

interface ExpandableDetailsSectionCommonProps {
  /** Renders the expanded body. Called with the field name(s) to exclude from the body. */
  renderBody: (excludeFieldNames: Array<string>) => React.ReactNode;
  /** Present when this section is an expandable array item: its in-header delete control and unique state key. */
  arrayExpandableItemControls?: ArrayExpandableItemControls;
}

interface EditorExpandableDetailsSectionProps extends ExpandableDetailsSectionCommonProps {
  expandableDetails: EditorExpandableDetailsConfig;
  /** The pre-built header field whose editor is lifted into the header row. */
  headerField: StepConfigField;
}

interface SummaryExpandableDetailsSectionProps extends ExpandableDetailsSectionCommonProps {
  expandableDetails: SummaryExpandableDetailsConfig;
  /** The pre-resolved header entries (value + kind) rendered in the read-only summary header. */
  summaryFields: Array<SummaryField>;
}

export type ExpandableDetailsSectionProps =
  | EditorExpandableDetailsSectionProps
  | SummaryExpandableDetailsSectionProps;

function isEditorHeader(props: ExpandableDetailsSectionProps): props is EditorExpandableDetailsSectionProps {
  return props.expandableDetails.headerMode === 'editor';
}

export default function ExpandableDetailsSection(props: ExpandableDetailsSectionProps): JSX.Element {
  const {renderBody} = props;
  const {clientStateId} = useStepConfigEditorContext();

  if (isEditorHeader(props)) {
    assert(
      props.headerField.systemVariableName === props.expandableDetails.headerField,
      `headerField prop (${props.headerField.systemVariableName}) must match expandableDetails.headerField (${props.expandableDetails.headerField})`
    );
  } else {
    assert(
      props.expandableDetails.headerFields.length > 0,
      'summary headerMode requires at least one headerField'
    );
  }

  const headerKey = isEditorHeader(props)
    ? props.expandableDetails.headerField
    : props.expandableDetails.headerFields.join('-');
  const stateKeySuffix = props.arrayExpandableItemControls?.stateKeySuffix;
  const stateKeyBase = stateKeySuffix != null ? `${headerKey}-${stateKeySuffix}` : headerKey;

  const [storedIsExpanded, setIsExpanded] = isExpandedGlobalState.useGlobalClientState(
    `expandable-${stateKeyBase}-${clientStateId}`
  );

  const defaultExpanded = props.expandableDetails.defaultExpanded ?? false;
  const isExpanded = storedIsExpanded ?? defaultExpanded;

  // In editor mode the body excludes the header field
  // In summary mode the header is read-only, so every field stays editable in the body.
  const excludeFieldNames = isEditorHeader(props) ? [props.expandableDetails.headerField] : [];

  const isInlineStyle =
    !isEditorHeader(props) || !props.headerField.editor.props.uiSchema?.fieldSettings?.renderCyclingSelector;

  return (
    <div className={css(expandableDetailStyles.expandableDetailsContainer)}>
      {isEditorHeader(props) && (
        <VariableHeaderOrNull
          variableName={props.headerField.variableName}
          infoText={props.headerField.infoText}
          addIcon={props.headerField.addIcon}
          isCompact={true}
        />
      )}
      <div className={css(expandableDetailStyles.rowWrapper)}>
        {isEditorHeader(props) ? (
          <div className={css(expandableDetailStyles.grow, expandableDetailStyles.headerField)}>
            {React.cloneElement(props.headerField.editor, {isExpanded})}
          </div>
        ) : (
          <SummaryHeader summaryFields={props.summaryFields} />
        )}
        {props.arrayExpandableItemControls?.deleteComponent != null && (
          <div className={css(expandableDetailStyles.deleteControl)}>
            {props.arrayExpandableItemControls.deleteComponent}
          </div>
        )}
        <IconButton isExpanded={isExpanded} setIsExpanded={setIsExpanded} isInlineStyle={isInlineStyle} />
      </div>

      {isExpanded && (
        <div className={css(expandableDetailStyles.detailFields)}>{renderBody(excludeFieldNames)}</div>
      )}
    </div>
  );
}

/** Config-shape kinds whose values stringify to a readable scalar in the summary header. */
const SUMMARY_RENDERABLE_KINDS: ReadonlySet<PipelineConfigJsonSpec['kind']> = new Set([
  'string',
  'color',
  'number',
  'integer',
  'boolean',
  'date',
  'enum',
]);

function renderSummaryValue(kind: PipelineConfigJsonSpec['kind'], value: JSONValue | null): React.ReactNode {
  switch (kind) {
    case 'color':
      return typeof value !== 'string' || value === '' ? null : (
        <span
          className={css(summaryStyles.colorDot)}
          style={{backgroundColor: value}}
          title={value}
          aria-label={value}
        />
      );
    case 'string':
      return typeof value !== 'string' || value === '' ? null : (
        <span className={css(summaryStyles.label)}>{value}</span>
      );
    default:
      assert(
        SUMMARY_RENDERABLE_KINDS.has(kind),
        `Summary header does not support rendering a "${kind}" field; only simple scalar kinds are supported`
      );
      return value == null || value === '' ? null : (
        <span className={css(summaryStyles.value)}>{String(value)}</span>
      );
  }
}

function SummaryHeader({summaryFields}: {summaryFields: Array<SummaryField>}): JSX.Element {
  return (
    <div className={css(expandableDetailStyles.grow, summaryStyles.header)}>
      {summaryFields.map((field, index) => (
        <React.Fragment key={index}>{renderSummaryValue(field.kind, field.value)}</React.Fragment>
      ))}
    </div>
  );
}

const expandableDetailStyles = StyleSheet.create({
  rowWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  grow: {
    flexGrow: 1,
  },
  headerField: {
    marginBottom: '-8px',
  },
  deleteControl: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    padding: '0 8px',
    border: `1px solid ${Colors.GRAY}`,
    borderLeft: 'none',
    borderRight: 'none',
  },
  expandableDetailsContainer: {
    marginBottom: 16,
  },
  detailFields: {
    padding: '8px 4px',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '0 0 4px 4px',
    backgroundColor: Colors.GRAY_LIGHTER,
  },
});

const summaryStyles = StyleSheet.create({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    border: `1px solid ${Colors.GRAY}`,
    borderRadius: '4px 0 0 4px',
    borderRight: 'none',
    minHeight: 28,
    minWidth: 0,
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  label: {
    fontWeight: 600,
    color: Colors.NEAR_BLACK,
    fontSize: 13,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  value: {
    color: Colors.GRAY_DARKER,
    fontSize: 13,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

function IconButton({
  isExpanded,
  setIsExpanded,
  isInlineStyle,
}: {
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  isInlineStyle: boolean;
}): JSX.Element {
  return (
    <div
      className={css(
        buttonStyles.base,
        isExpanded && buttonStyles.expandedColors,
        !isExpanded && buttonStyles.notExpandedColors,

        !isInlineStyle && isExpanded && buttonStyles.tabExpandedBorder,
        !isInlineStyle && !isExpanded && buttonStyles.tabNotExpandedBorder,

        isInlineStyle && buttonStyles.inlineVariantBorder,
        isInlineStyle && isExpanded && buttonStyles.inlineExpandedBorder,
        isInlineStyle && !isExpanded && buttonStyles.inlineNotExpandedBorder
      )}
      onClick={() => {
        setIsExpanded(!isExpanded);
      }}
    >
      <BenchlingIcon icon="filter" />
    </div>
  );
}

const buttonStyles = StyleSheet.create({
  base: {
    width: 28,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderStyle: 'solid',
    borderWidth: '1px',
  },

  expandedColors: {
    borderColor: Colors.BLUE_LIGHTEST,
    color: Colors.BLUE_DARKER,
    backgroundColor: Colors.BLUE_POWDER,
  },
  notExpandedColors: {
    borderColor: Colors.GRAY,
    color: Colors.GRAY_DARKER,
    backgroundColor: Colors.GRAY_LIGHT,
  },

  tabExpandedBorder: {
    borderBottomWidth: '0',
    borderRadius: '4px 4px 0 0',
  },
  tabNotExpandedBorder: {
    borderRadius: '4px',
  },

  inlineVariantBorder: {
    borderWidth: '1px 1px 1px 0',
  },
  inlineExpandedBorder: {
    borderRadius: '0 4px 0 0',
    borderBottomWidth: '0',
  },
  inlineNotExpandedBorder: {
    borderRadius: '0 4px 4px 0',
  },
});
